// Mobile full-nav drawer (Q11 — DESIGN-DECISIONS-2026-05-01).
// Slides in from the right. Mirrors Sidebar structure (HCM mark + nav list +
// "Powered by Nsofar" footer). Opens via "More" tab in BottomNav or hamburger
// in TopHeader.

import { useEffect } from "react";
import type { JSX } from "react";
import { NavLink } from "react-router-dom";
import { useVisibleNav } from "./Sidebar.js";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function MobileNavDrawer({ open, onClose }: Props): JSX.Element {
  const nav = useVisibleNav();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      {open && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/30"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={`md:hidden fixed right-0 top-0 h-full w-72 bg-white shadow-xl border-l border-slate-200 z-50 flex flex-col transition-transform duration-200 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        role="dialog"
        aria-label="Navigation"
        aria-hidden={!open}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-slate-100 shrink-0">
          <span className="text-lg font-bold text-navy-700">HCM</span>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-xl leading-none"
            aria-label="Close navigation"
          >
            ×
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={onClose}
              className={({ isActive }) =>
                `block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-navy-50 text-navy-800"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-slate-100 shrink-0">
          <a
            href="https://nsofar.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            Powered by Nsofar
          </a>
        </div>
      </aside>
    </>
  );
}
